import {z as zod} from "zod";
import {PaymentCurrency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentCurrency";
import {PaymentFrequency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentFrequency";
import {
    type SubscriptionPlan,
    subscriptionPlanZodValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/SubscriptionPlan";
import type {Integer, Iso8601Date, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    integerZodValidator,
    iso8601DateTimeZodValidator,
    iso8601DateZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";

export enum SubscriptionCycleStatus {
    activeAndInitialPaymentNotCollected = "56573792-0a24-4263-9073-f8a67493d95d",
    activeAndPaid = "67046d1c-ebf9-437e-b74c-24c4a644c8dd",
    activeAndPaymentPending = "ddffe0ff-9a05-415f-b8e5-d35eec6147cd",
    cancelledBeforeStarting = "046c5e80-d1c3-4afb-bb28-06007c8eb106",
    inGracePeriod = "368ea7bb-2896-4749-90cd-6d2863ac85a7",
    terminatedDueToNonPayment = "6b70c9cb-bc16-4994-8d52-f78f611cc02f",
    terminatedDueToTime = "d392d99b-968b-4988-85a0-e477db28ed08",
    trialActive = "cd296f07-ec39-49ad-9be1-8a5b3dcecf4d",
    trialEndedDueToPurchase = "acc65d0b-efab-4c03-b15d-60b4e1df4a2d",
    trialEndedDueToTime = "bfc49349-9a61-4846-900a-9181d81adeee",
}

export enum SubscriptionStatus {
    cancelled = "5173600b-b6ea-4231-a9be-a1b856267c5e",
    markedForCancellation = "e287eaca-7e5b-4b3e-bcd4-8891bf481b02",
    markedForDowngrade = "4dcaa656-c7d0-4bfc-ab48-501d48dfa6b9",
    planActive = "c01858f1-b7eb-4729-8a80-35d16992018f",
    planActiveInGracePeriod = "e9b5a286-509a-4c4f-ba89-8d5a1b15231e",
    planActivePaymentPending = "22e820ca-5d72-4cfd-8406-30ca9aa4c74b",
    planScheduled = "bfbdfc72-ad67-4d17-a8d3-57420a0f398d",
    terminatedDueToNonPayment = "ff005407-6bcb-4191-9736-1727655d4cb3",
    trialActive = "74d6e738-a2e8-45d7-a3ac-d0da7880f5f8",
    trialEndedDueToPurchase = "1c875a21-f7b7-4e25-b61e-b9356ec42e0c",
    trialEndedDueToTime = "fd8686b9-b1f1-4c1f-b629-18f08a199b78",
}

export type Subscription = {
    endDate: Iso8601Date | null;
    id: Uuid;
    lastCronTimestamp: Iso8601DateTime;
    paymentAmount: Integer | null;
    paymentCurrency: PaymentCurrency | null;
    paymentFrequency: PaymentFrequency | null;
    paymentRenewalDetails: {
        paymentSessionId: Uuid;
    } | null;
    startDate: Iso8601Date;
    status: SubscriptionStatus;
    subscriptionPlanId: Uuid;
    tags: Array<string>;
    workspaceId: Uuid;
};

export type SubscriptionCycle = {
    endDate: Iso8601Date;
    id: Uuid;
    paymentDetails: Record<string, unknown> | null;
    startDate: Iso8601Date;
    status: SubscriptionCycleStatus;
    subscriptionId: Uuid;
};

export type SubscriptionDetails = {
    currentSubscription: Subscription;
    currentSubscriptionCycle: SubscriptionCycle;
    currentSubscriptionPlan: SubscriptionPlan;
    id: Uuid;
    timeZoneFixedOffsetIdentifier: string;
};

export type SubscriptionWithPlanDetails = Subscription & Omit<SubscriptionPlan, "id"> & {
    expectedEndDate: Iso8601Date;
    isActive: boolean;
    isFreeTrial: boolean;
};

export const subscriptionZodValidator = zod.object({
    endDate: iso8601DateZodValidator.nullable(),
    id: uuidZodValidator,
    lastCronTimestamp: iso8601DateTimeZodValidator,
    paymentAmount: integerZodValidator.nullable(),
    paymentCurrency: zod.enum(PaymentCurrency).nullable(),
    paymentFrequency: zod.enum(PaymentFrequency).nullable(),
    paymentRenewalDetails: zod
        .object({
            paymentSessionId: uuidZodValidator,
        })
        .nullable(),
    startDate: iso8601DateZodValidator,
    status: zod.enum(SubscriptionStatus),
    subscriptionPlanId: uuidZodValidator,
    tags: zod.array(zod.string()),
    workspaceId: uuidZodValidator,
});

export const subscriptionCycleZodValidator = zod.object({
    endDate: iso8601DateZodValidator,
    id: uuidZodValidator,
    paymentDetails: zod.record(zod.string(), zod.unknown()).nullable(),
    startDate: iso8601DateZodValidator,
    status: zod.enum(SubscriptionCycleStatus),
    subscriptionId: uuidZodValidator,
});

export const subscriptionDetailsZodValidator = zod.object({
    currentSubscription: subscriptionZodValidator,
    currentSubscriptionCycle: subscriptionCycleZodValidator,
    currentSubscriptionPlan: subscriptionPlanZodValidator,
    id: uuidZodValidator,
    timeZoneFixedOffsetIdentifier: zod.string(),
});

export const subscriptionWithPlanDetailsZodValidator = subscriptionZodValidator
    .merge(subscriptionPlanZodValidator.omit({id: true}))
    .extend({
        expectedEndDate: iso8601DateZodValidator,
        isActive: zod.boolean(),
        isFreeTrial: zod.boolean(),
    });
